import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Chip, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  IconButton
} from '@mui/material';

import { 
  Add as AddIcon, 
  Message as MessageIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  VisibilityOff as AnonymousIcon,
  Send as SendIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getApiUrl, API_ENDPOINTS } from '../config/api';

interface Complaint {
  id: string;
  title: string;
  description: string;
  category: 'workplace' | 'harassment' | 'discrimination' | 'safety' | 'management' | 'other';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in_review' | 'resolved' | 'closed';
  isAnonymous: boolean;
  submitterId: string | null;
  assignedAdminId: string | null;
  assignedAt: string | null;
  resolvedAt: string | null;
  anonymousToken: string | null;
  createdAt: string;
  updatedAt: string;
  submitter: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  assignedAdmin: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  messages?: ComplaintMessage[];
}

interface ComplaintMessage {
  id: string;
  complaintId: string;
  senderId: string | null;
  senderType: 'user' | 'admin' | 'anonymous' | 'system';
  message: string;
  isInternal: boolean;
  attachmentPath: string | null;
  createdAt: string;
  sender: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
}

const Skaistykla: React.FC = () => {
  const { user, token, hasPermission } = useAuth();
  const { t } = useTranslation();
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Form states
  const [complaintForm, setComplaintForm] = useState({
    title: '',
    description: '',
    category: 'other' as const,
    priority: 'medium' as const,
    isAnonymous: false
  });

  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    fetchComplaints();
  }, [activeTab]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [selectedComplaint?.messages]);

  const fetchComplaints = async () => {
    try {
      setLoading(true);
      setError(null);

      let endpoint = API_ENDPOINTS.COMPLAINTS;
      if (activeTab === 1) {
        endpoint = API_ENDPOINTS.COMPLAINTS_MY_COMPLAINTS;
      } else if (activeTab === 2) {
        endpoint = API_ENDPOINTS.COMPLAINTS_ASSIGNED;
      }

      const response = await fetch(getApiUrl(endpoint), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(t('skaistykla.messages.failedToLoad'));
      }

      const data = await response.json();
      setComplaints(data.complaints);
    } catch (err) {
      console.error('Error fetching complaints:', err);
      setError(t('skaistykla.messages.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateComplaint = async () => {
    try {
      setSubmitting(true);

      const response = await fetch(getApiUrl(API_ENDPOINTS.COMPLAINTS), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(complaintForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('skaistykla.messages.failedToCreate'));
      }

      const data = await response.json();
      
      if (complaintForm.isAnonymous && data.anonymousToken) {
        setError(t('skaistykla.messages.anonymousComplaintSubmitted', { token: data.anonymousToken }));
      }

      await fetchComplaints();
      setCreateDialogOpen(false);
      setComplaintForm({
        title: '',
        description: '',
        category: 'other',
        priority: 'medium',
        isAnonymous: false
      });
    } catch (err) {
      console.error('Error creating complaint:', err);
      setError(err instanceof Error ? err.message : t('skaistykla.messages.failedToCreate'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignComplaint = async (complaintId: string) => {
    try {
      setSubmitting(true);

      const response = await fetch(getApiUrl(API_ENDPOINTS.COMPLAINT_ASSIGN(complaintId)), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('skaistykla.messages.failedToAssign'));
      }

      await fetchComplaints();
    } catch (err) {
      console.error('Error assigning complaint:', err);
      setError(err instanceof Error ? err.message : t('skaistykla.messages.failedToAssign'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (complaintId: string, status: string) => {
    try {
      setSubmitting(true);

      const response = await fetch(getApiUrl(API_ENDPOINTS.COMPLAINT_STATUS(complaintId)), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('skaistykla.messages.failedToUpdateStatus'));
      }

      await fetchComplaints();
      if (selectedComplaint && selectedComplaint.id === complaintId) {
        await fetchComplaintDetails(complaintId);
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err instanceof Error ? err.message : t('skaistykla.messages.failedToUpdateStatus'));
    } finally {
      setSubmitting(false);
    }
  };

  const fetchComplaintDetails = async (complaintId: string) => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.COMPLAINT(complaintId)), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedComplaint(data.complaint);
      }
    } catch (err) {
      console.error('Error fetching complaint details:', err);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedComplaint || !newMessage.trim()) return;

    try {
      setSubmitting(true);

      const response = await fetch(getApiUrl(API_ENDPOINTS.COMPLAINT_MESSAGES(selectedComplaint.id)), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          message: newMessage
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('skaistykla.messages.failedToSendMessage'));
      }

      setNewMessage('');
      await fetchComplaintDetails(selectedComplaint.id);
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err instanceof Error ? err.message : t('skaistykla.messages.failedToSendMessage'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComplaint = async (complaintId: string) => {
    if (!window.confirm(t('skaistykla.dialogs.deleteConfirm'))) {
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(getApiUrl(API_ENDPOINTS.COMPLAINT(complaintId)), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('skaistykla.messages.failedToDelete'));
      }

      setSuccess(t('skaistykla.messages.complaintDeleted'));
      fetchComplaints(); // Refresh the complaints list
    } catch (err) {
      console.error('Error deleting complaint:', err);
      setError(err instanceof Error ? err.message : t('skaistykla.messages.failedToDelete'));
    } finally {
      setSubmitting(false);
    }
  };

  const openMessageDialog = (complaint: Complaint) => {
    setSelectedComplaint(complaint);
    fetchComplaintDetails(complaint.id);
    setMessageDialogOpen(true);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'in_review': return 'info';
      case 'resolved': return 'success';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'harassment': return <WarningIcon />;
      case 'safety': return <WarningIcon />;
      default: return <MessageIcon />;
    }
  };

  const formatMessageSender = (message: ComplaintMessage) => {
    if (message.senderType === 'system') return t('skaistykla.dialogs.system');
    if (message.senderType === 'anonymous') return t('skaistykla.category.anonymous');
    if (message.sender) {
      return `${message.sender.firstName} ${message.sender.lastName}`;
    }
    return t('skaistykla.dialogs.unknown');
  };

  const translateStatus = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'pending': t('skaistykla.status.pending'),
      'in_review': t('skaistykla.status.inReview'),
      'resolved': t('skaistykla.status.resolved'),
      'closed': t('skaistykla.status.closed')
    };
    return statusMap[status] || status.toUpperCase();
  };

  const translatePriority = (priority: string) => {
    const priorityMap: { [key: string]: string } = {
      'low': t('skaistykla.priority.low'),
      'medium': t('skaistykla.priority.medium'),
      'high': t('skaistykla.priority.high'),
      'urgent': t('skaistykla.priority.urgent')
    };
    return priorityMap[priority] || priority.toUpperCase();
  };

  const translateCategory = (category: string) => {
    const categoryMap: { [key: string]: string } = {
      'workplace': t('skaistykla.category.workplace'),
      'harassment': t('skaistykla.category.harassment'),
      'discrimination': t('skaistykla.category.discrimination'),
      'safety': t('skaistykla.category.safety'),
      'management': t('skaistykla.category.management'),
      'other': t('skaistykla.category.other')
    };
    return categoryMap[category] || category;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Check permissions - users need complaints.view permission
  const canViewComplaints = hasPermission('complaints.view');
  
  if (!canViewComplaints) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Jūs neturite prieigos prie šio skyriaus. Reikalingas leidimas: complaints.view
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('skaistykla.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          {t('skaistykla.newComplaint')}
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label={t('skaistykla.tabs.allComplaints')} />
          <Tab label={t('skaistykla.tabs.myComplaints')} />
          {hasPermission('complaints.manage') && <Tab label={t('skaistykla.tabs.assignedToMe')} />}
        </Tabs>
      </Box>

      {/* Complaints List */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
        gap: 2 
      }}>
        {complaints.map((complaint) => (
          <Card key={complaint.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              {/* Header with priority and category */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Chip
                  label={translatePriority(complaint.priority)}
                  color={getPriorityColor(complaint.priority) as any}
                  size="small"
                />
                <Box display="flex" alignItems="center">
                  {complaint.isAnonymous ? <AnonymousIcon /> : getCategoryIcon(complaint.category)}
                  <Typography variant="caption" sx={{ ml: 0.5 }}>
                    {complaint.isAnonymous ? t('skaistykla.category.anonymous') : translateCategory(complaint.category)}
                  </Typography>
                </Box>
              </Box>

              {/* Title and Status */}
              <Typography variant="h6" gutterBottom>
                {complaint.title}
              </Typography>
              <Chip
                label={translateStatus(complaint.status)}
                color={getStatusColor(complaint.status) as any}
                size="small"
                sx={{ mb: 2 }}
              />

              {/* Description */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {complaint.description.length > 150 
                  ? `${complaint.description.substring(0, 150)}...` 
                  : complaint.description}
              </Typography>

              {/* Submitter and Assigned Admin */}
              <Box mt={2}>
                {!complaint.isAnonymous && complaint.submitter && (
                  <Typography variant="caption" color="text.secondary">
                    {t('skaistykla.labels.submittedBy')} {complaint.submitter.firstName} {complaint.submitter.lastName}
                  </Typography>
                )}
                {complaint.assignedAdmin && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    {t('skaistykla.labels.assignedTo')} {complaint.assignedAdmin.firstName} {complaint.assignedAdmin.lastName}
                  </Typography>
                )}
              </Box>
            </CardContent>

            {/* Actions */}
            <Box p={2} pt={0}>
              {hasPermission('complaints.manage') && !complaint.assignedAdminId && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  onClick={() => handleAssignComplaint(complaint.id)}
                  disabled={submitting}
                  sx={{ mb: 1 }}
                >
                  {t('skaistykla.buttons.assignToMe')}
                </Button>
              )}
              
              <Button
                fullWidth
                variant="outlined"
                startIcon={<MessageIcon />}
                onClick={() => openMessageDialog(complaint)}
                sx={{ mb: 1 }}
              >
                {t('skaistykla.buttons.viewMessages')}
              </Button>

              {hasPermission('complaints.manage') && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {complaint.status !== 'resolved' && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      onClick={() => handleUpdateStatus(complaint.id, 'resolved')}
                      disabled={submitting}
                    >
                      {t('skaistykla.buttons.resolve')}
                    </Button>
                  )}
                  {complaint.status !== 'closed' && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={() => handleUpdateStatus(complaint.id, 'closed')}
                      disabled={submitting}
                    >
                      {t('skaistykla.buttons.close')}
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteComplaint(complaint.id)}
                    disabled={submitting}
                  >
                    {t('skaistykla.buttons.delete')}
                  </Button>
                </Box>
              )}
            </Box>
          </Card>
        ))}
      </Box>

      {complaints.length === 0 && !loading && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            {t('skaistykla.messages.noComplaintsFound')}
          </Typography>
        </Box>
      )}

      {/* Create Complaint Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('skaistykla.dialogs.submitNewComplaint')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('skaistykla.form.title')}
            fullWidth
            variant="outlined"
            value={complaintForm.title}
            onChange={(e) => setComplaintForm({ ...complaintForm, title: e.target.value })}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label={t('skaistykla.form.description')}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={complaintForm.description}
            onChange={(e) => setComplaintForm({ ...complaintForm, description: e.target.value })}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>{t('skaistykla.form.category')}</InputLabel>
              <Select
                value={complaintForm.category}
                label={t('skaistykla.form.category')}
                onChange={(e) => setComplaintForm({ ...complaintForm, category: e.target.value as any })}
              >
                <MenuItem value="workplace">{t('skaistykla.category.workplace')}</MenuItem>
                <MenuItem value="harassment">{t('skaistykla.category.harassment')}</MenuItem>
                <MenuItem value="discrimination">{t('skaistykla.category.discrimination')}</MenuItem>
                <MenuItem value="safety">{t('skaistykla.category.safety')}</MenuItem>
                <MenuItem value="management">{t('skaistykla.category.management')}</MenuItem>
                <MenuItem value="other">{t('skaistykla.category.other')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('skaistykla.form.priority')}</InputLabel>
              <Select
                value={complaintForm.priority}
                label={t('skaistykla.form.priority')}
                onChange={(e) => setComplaintForm({ ...complaintForm, priority: e.target.value as any })}
              >
                <MenuItem value="low">{t('skaistykla.priority.low')}</MenuItem>
                <MenuItem value="medium">{t('skaistykla.priority.medium')}</MenuItem>
                <MenuItem value="high">{t('skaistykla.priority.high')}</MenuItem>
                <MenuItem value="urgent">{t('skaistykla.priority.urgent')}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={complaintForm.isAnonymous}
                onChange={(e) => setComplaintForm({ ...complaintForm, isAnonymous: e.target.checked })}
              />
            }
            label={t('skaistykla.form.submitAnonymously')}
          />
          
          {complaintForm.isAnonymous && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                {t('skaistykla.messages.anonymousWarning')}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('skaistykla.buttons.cancel')}</Button>
          <Button 
            onClick={handleCreateComplaint} 
            variant="contained"
            disabled={!complaintForm.title.trim() || !complaintForm.description.trim() || submitting}
          >
            {submitting ? <CircularProgress size={20} /> : t('skaistykla.buttons.submitComplaint')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Message Dialog */}
      <Dialog open={messageDialogOpen} onClose={() => setMessageDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedComplaint && (
            <Box>
              <Typography variant="h6">{selectedComplaint.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('skaistykla.labels.status')} {translateStatus(selectedComplaint.status)} | {t('skaistykla.labels.priority')} {translatePriority(selectedComplaint.priority)}
              </Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedComplaint && (
            <>
              {/* Messages List */}
              <Paper sx={{ maxHeight: 400, overflow: 'auto', mb: 2, p: 1 }}>
                <List>
                  {selectedComplaint.messages?.map((message) => (
                    <ListItem key={message.id} sx={{ 
                      flexDirection: 'column', 
                      alignItems: 'flex-start',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1
                    }}>
                      <Box display="flex" justifyContent="space-between" width="100%" mb={1}>
                        <Typography variant="subtitle2" color="primary">
                          {formatMessageSender(message)}
                          {message.senderType === 'admin' && <AdminIcon fontSize="small" sx={{ ml: 0.5 }} />}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(message.createdAt).toLocaleString()}
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        {message.message}
                      </Typography>
                    </ListItem>
                  ))}
                  <div ref={messagesEndRef} />
                </List>
              </Paper>

              {/* Send Message */}
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder={t('skaistykla.form.messagePlaceholder')}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  sx={{ mb: 2 }}
                />
                
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || submitting}
                  >
                    {t('skaistykla.buttons.sendMessage')}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageDialogOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Skaistykla;